import {
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { Tooltip, IconProps, Typography, TypographyProps } from '@mui/material';
import cx from 'classnames';
import { fill, range, truncate as _truncate } from 'lodash';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { IconSize, ICON_SIZE_MAP } from 'constants/icon-size.constants';
import { TranslationNamespace } from 'i18n';

const SECRET_MASK = '••••••••';

type Props = {
  text: string;
  label?: ReactNode;
  iconOnly?: boolean;
  disabled?: boolean;
  className?: string;
  truncateLength?: number;
  truncate?: number | boolean;
  iconColor?: IconProps['color'];
  labelTextColor?: TypographyProps['color'];
  iconSize?: IconSize;
  hidden?: boolean;
  secret?: boolean;
  children?: React.ReactNode;
};

export const CopyText: React.FC<Props> = ({
  text,
  truncate,
  truncateLength: truncateLengthProp,
  label: labelProp,
  iconOnly,
  className,
  children,
  disabled,
  iconColor = 'secondary',
  labelTextColor,
  iconSize = 'sm',
  hidden,
  secret,
}: Props) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.copy_text',
  });

  const [revealed, setRevealed] = useState(false);

  const truncateLength = useMemo(
    () => (truncate ? truncateLengthProp || 10 : truncateLengthProp),
    [truncate, truncateLengthProp],
  );

  const label = useMemo(
    () =>
      labelProp ||
      (truncateLength
        ? _truncate(text, { length: truncateLength || 10 })
        : text),
    [labelProp, text, truncateLength],
  );

  const isHidden = useMemo(
    () => (secret ? !revealed : hidden),
    [secret, revealed, hidden],
  );

  const content = useMemo(() => {
    if (isHidden) {
      return secret
        ? SECRET_MASK
        : fill(range(truncateLength || text.length), '*');
    }
    return label || text;
  }, [isHidden, secret, label, text, truncateLength]);

  const onClick = useCallback(
    (event: React.MouseEvent) => {
      if (!disabled) {
        event.stopPropagation();
      }
    },
    [disabled],
  );

  const onCopy = useCallback(() => {
    if (!disabled) {
      toast(t('notification'));
    }
  }, [disabled, t]);

  const handleToggleReveal = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setRevealed((prev) => !prev);
  }, []);

  const copyNode = useMemo(
    () => (
      <CopyToClipboard text={text} onCopy={onCopy}>
        <Tooltip title={disabled ? null : t('title')}>
          <span>
            {children || (
              <span
                className={cx('tw-inline-flex tw-items-center', {
                  'tw-cursor-pointer': !disabled,
                })}
              >
                {!iconOnly && (
                  <Typography
                    color={labelTextColor}
                    marginRight={!iconOnly ? 1 : 0}
                  >
                    {content}
                  </Typography>
                )}
                {!disabled && (
                  <ContentCopyIcon
                    sx={{
                      fontSize: ICON_SIZE_MAP[iconSize],
                    }}
                    color={iconColor}
                  />
                )}
              </span>
            )}
          </span>
        </Tooltip>
      </CopyToClipboard>
    ),
    [
      text,
      onCopy,
      disabled,
      t,
      children,
      iconOnly,
      labelTextColor,
      content,
      iconSize,
      iconColor,
    ],
  );

  return (
    <span className={className} onClick={onClick}>
      {secret ? (
        <span className="tw-inline-flex tw-items-center">
          {copyNode}
          <span
            className="tw-inline-flex tw-cursor-pointer tw-items-center"
            onClick={handleToggleReveal}
          >
            {revealed ? (
              <VisibilityOffIcon
                sx={{ fontSize: ICON_SIZE_MAP[iconSize], ml: 0.5 }}
                color={iconColor}
              />
            ) : (
              <VisibilityIcon
                sx={{ fontSize: ICON_SIZE_MAP[iconSize], ml: 0.5 }}
                color={iconColor}
              />
            )}
          </span>
        </span>
      ) : (
        copyNode
      )}
    </span>
  );
};
